import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@lightspeed/flame/Button";
import {
  buildD365DimensionValue,
  getD365DimensionLevels,
  parseD365DimensionValue,
} from "../../utils/d365Dimensions";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(15, 23, 42, 0.45)",
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
};

const modalStyle = {
  width: "100%",
  maxWidth: 720,
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 24px 80px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const selectStyle = {
  width: "100%",
  minHeight: 36,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "6px 8px",
  color: "#0f172a",
  background: "#fff",
};

const D365DimensionsModal = ({ open, value, dimensions, onClose, onApply }) => {
  const levels = useMemo(() => getD365DimensionLevels(dimensions), [dimensions]);
  const [selectedByLevel, setSelectedByLevel] = useState({});

  useEffect(() => {
    if (open) setSelectedByLevel(parseD365DimensionValue(value));
  }, [open, value]);

  if (!open) return null;

  const rowsByLevel = levels.reduce((acc, level) => {
    acc[level] = (dimensions || []).filter((row) => row.dimension_level === level);
    return acc;
  }, {});

  const normalizeValue = (fieldValue) => String(fieldValue || "").trim().toLowerCase();

  const getSelectedMatch = (level) => {
    const selected = selectedByLevel[level];
    if (!selected) return null;

    const selectedId = normalizeValue(selected.id);
    const selectedLevel = normalizeValue(selected.dimension_level || level);

    return (rowsByLevel[level] || []).find(
      (row) =>
        normalizeValue(row.dimension_level) === selectedLevel &&
        normalizeValue(row.id) === selectedId
    );
  };

  const getSelectedOptionValue = (level) => {
    const selected = selectedByLevel[level];
    if (!selected) return "";

    const match = getSelectedMatch(level);
    return match?.id || selected.id || "";
  };

  const getSelectedLabel = (selected) => {
    if (!selected) return "";
    const name = selected.displayName || selected.description || selected.id || "";
    return selected.code ? `${selected.code} - ${name}` : name;
  };

  const handleApply = () => {
    const canonicalSelectedByLevel = levels.reduce((acc, level) => {
      const selected = selectedByLevel[level];
      if (selected) acc[level] = getSelectedMatch(level) || selected;
      return acc;
    }, {});

    onApply(buildD365DimensionValue(canonicalSelectedByLevel));
    onClose();
  };

  const handleClear = () => {
    setSelectedByLevel({});
  };

  return (
    <div onClick={onClose} style={overlayStyle}>
      <div onClick={(event) => event.stopPropagation()} style={modalStyle}>
        <div
          style={{
            padding: "16px 20px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0f172a" }}>
              D365 Dimensions
            </div>
            <button
              type="button"
              onClick={handleClear}
              style={{
                border: "1px solid #dc2626",
                background: "#dc2626",
                color: "#fff",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 12,
                fontWeight: 700,
                padding: "4px 8px",
              }}
            >
              Clear
            </button>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: "transparent",
              fontSize: 20,
              cursor: "pointer",
              color: "#64748b",
            }}
          >
            &times;
          </button>
        </div>

        <div style={{ padding: 20 }}>
          {levels.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: 14 }}>
              No D365 dimensions returned from API.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: 14,
              }}
            >
              {levels.map((level) => {
                const selected = selectedByLevel[level];
                const selectedMatch = getSelectedMatch(level);
                const selectedValue = getSelectedOptionValue(level);

                return (
                  <label key={level} style={{ display: "grid", gap: 6, color: "#1e293b" }}>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>{level}</span>
                    <select
                      value={selectedValue}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        const selectedRow = rowsByLevel[level].find(
                          (row) =>
                            normalizeValue(row.dimension_level) === normalizeValue(level) &&
                            String(row.id) === nextValue
                        );

                        setSelectedByLevel((current) => {
                          if (!nextValue) {
                            const next = { ...current };
                            delete next[level];
                            return next;
                          }

                          return {
                            ...current,
                            [level]: selectedRow || current[level],
                          };
                        });
                      }}
                      style={selectStyle}
                    >
                      <option value="">Select {level}</option>
                      {selected && !selectedMatch && selectedValue && (
                        <option value={selectedValue}>{getSelectedLabel(selected)}</option>
                      )}
                      {rowsByLevel[level].map((row) => (
                        <option key={row.id} value={row.id}>
                          {row.code ? `${row.code} - ${row.displayName}` : row.displayName}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div
          style={{
            padding: "16px 20px",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <Button size="small" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button size="small" fill="true" disabled={levels.length === 0} onClick={handleApply}>
            Apply
          </Button>
        </div>
      </div>
    </div>
  );
};

export default D365DimensionsModal;
